import React from 'react';
import { Plus, Edit, Trash2, RefreshCw, Globe, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import { LoadingSpinner } from '../../ui/Loading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/Table';
import { Domain } from '../../../types';
import { useDomains } from '../../../hooks/useApi';
import { useAuth } from '../../../hooks/useAuth';
import { useFormatDate } from '../../../hooks/useFormatDate';

interface DomainListProps {
  onCreate: () => void;
  onEdit: (domain: Domain) => void;
  onDelete: (domain: Domain) => void;
}

const DomainList: React.FC<DomainListProps> = ({ onCreate, onEdit, onDelete }) => {
  const { user: currentAdmin } = useAuth();
  const fmt = useFormatDate();
  const { data, isLoading, error, refetch } = useDomains();
  const canManage = currentAdmin?.role === 'system_admin' || currentAdmin?.role === 'super_admin';

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive">Failed to load domains</p>
          <button onClick={() => refetch()} className="mt-2 text-sm text-primary hover:underline">
            Try again
          </button>
        </div>
      </div>
    );
  }

  const domains = data?.data || [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center">
          <Globe className="h-5 w-5 mr-2" />
          Domains ({domains.length})
        </CardTitle>
        <div className="flex gap-2">
          {canManage && (
            <Button onClick={onCreate} className="flex items-center space-x-2">
              <Plus className="h-4 w-4" />
              <span>Add Domain</span>
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner />
          </div>
        ) : domains.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No domains registered yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Domain</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Shop</TableHead>
                  <TableHead>Games</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {domains.map((domain) => (
                  <TableRow key={domain.id} className="hover:bg-accent/50">
                    <TableCell className="font-mono font-medium">{domain.domain}</TableCell>
                    <TableCell>
                      {domain.isActive ? (
                        <span className="flex items-center gap-1 text-green-600 dark:text-green-400 text-sm">
                          <CheckCircle className="h-4 w-4" /> Active
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-muted-foreground text-sm">
                          <XCircle className="h-4 w-4" /> Inactive
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs font-mono text-muted-foreground">
                      {domain.shopId ? domain.shopId : '—'}
                    </TableCell>
                    <TableCell className="text-xs">
                      {(domain.gameMappingCount ?? 0) === 0 ? 'ALL' : domain.gameMappingCount}
                    </TableCell>
                    <TableCell>{fmt(domain.created_at, { second: undefined })}</TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Button variant="outline" size="sm" onClick={() => onEdit(domain)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {canManage && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onDelete(domain)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DomainList;
